/**
 * The charts of the Library as a grid of cards or a list of rows, grouped by song or not (W2-10 projects-data;
 * DESIGN.md §4.1).
 *
 * Keyboard (one tab stop for the whole list, roving focus): arrow keys move between charts (in the grid, Up and Down
 * go to the card above / below), Home / End to the first / last, Enter (or Space) opens, F2 renames, Delete moves the
 * chart to the Trash, and typing a letter starts a search with it. When the focused chart leaves the list (Delete,
 * "Move to Trash" in its menu or the palette, another tab), the keyboard stays in the list on the next chart shown
 * (the previous one at the end), and so does the list's tab stop.
 *
 * The cards are `[data-keys="native"]` widgets (ProjectCard): the app's KeyboardRouter gives their unmodified keys to
 * this handler first (bubble phase) and routes a key to the global bindings (playback seeks on the arrows, Space,
 * tool letters, F2 workspace, ...) only when the handler left it alone (did not preventDefault). Ctrl / Alt chords
 * keep reaching the app's commands (save, palette, undo).
 */

import { useCallback, useLayoutEffect, useMemo, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { ProjectId } from '../../core/model/types';
import type { ProjectSummary } from '../../persist/types';
import { useFocusHandoff } from './focusHandoff';
import type { LibraryText } from './hooks';
import { m } from './library.messages';
import { openProject, renameProject, trashProject } from './libraryActions';
import { groupBySong, sortProjects, successorOf, type LibrarySort, type SongGroupModel } from './libraryModel';
import { libraryStore, liveProjects } from './libraryStore';
import { ProjectCard } from './ProjectCard';
import { SongGroup } from './SongGroup';
import s from './Library.module.css';

export interface ProjectGridProps {
  readonly services: AppServices;
  readonly text: LibraryText;
  /** Charts shown (search applied). */
  readonly projects: readonly ProjectSummary[];
  /** Charts per song in the whole Library (group headers count every difficulty, not only the matches). */
  readonly songCounts: ReadonlyMap<string, number>;
  readonly groupBySong: boolean;
  readonly sort: LibrarySort;
  readonly view: 'grid' | 'list';
  readonly now: number;
  readonly focused: ProjectId | null;
  readonly renaming: ProjectId | null;
  readonly busy: ReadonlySet<ProjectId>;
  /** A printable key typed on a card: continue in the search field. */
  readonly onTypeToSearch: (char: string) => void;
}

/** What the page shows: songs with several difficulties under a header, other charts flowing together. */
export type LayoutSegment =
  | {
      readonly kind: 'song';
      readonly key: string;
      readonly group: SongGroupModel;
      readonly projects: readonly ProjectSummary[];
    }
  | { readonly kind: 'flow'; readonly key: string; readonly projects: readonly ProjectSummary[] };

/** Consecutive songs with a single chart in the whole Library merge into one flow segment (order kept). */
export function layoutSegments(groups: readonly SongGroupModel[], groupBySong: boolean): LayoutSegment[] {
  const out: LayoutSegment[] = [];
  let flow: ProjectSummary[] | null = null;
  for (const g of groups) {
    if (groupBySong && g.total > 1) {
      flow = null;
      out.push({ kind: 'song', key: g.key, group: g, projects: g.projects });
      continue;
    }
    if (flow === null) {
      flow = [...g.projects];
      out.push({ kind: 'flow', key: `flow:${g.key}`, projects: flow });
    } else flow.push(...g.projects);
  }
  return out;
}

/** The card a geometric Up / Down move lands on (grid view); null when layout is unknown. */
function verticalNeighbour(cards: readonly HTMLElement[], from: HTMLElement, dir: 1 | -1): HTMLElement | null {
  const a = from.getBoundingClientRect();
  if (a.width === 0 && a.height === 0) return null;
  const ax = a.left + a.width / 2;
  let best: HTMLElement | null = null;
  let bestScore = Infinity;
  for (const c of cards) {
    if (c === from) continue;
    const b = c.getBoundingClientRect();
    const dy = dir === 1 ? b.top - a.bottom : a.top - b.bottom;
    if (dy < -1) continue;
    const dx = Math.abs(b.left + b.width / 2 - ax);
    const score = Math.max(0, dy) * 4 + dx;
    if (score < bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

export function ProjectGrid(props: ProjectGridProps) {
  const { services, text, projects, view, now, focused, renaming, busy, sort } = props;
  const root = useRef<HTMLDivElement>(null);
  const store = libraryStore(services);

  const groups: readonly SongGroupModel[] = useMemo(
    () =>
      props.groupBySong
        ? groupBySong(projects, sort, text.locale, props.songCounts)
        : [
            {
              key: 'all',
              musicAsset: null,
              title: '',
              composer: '',
              projects: sortProjects(projects, sort, text.locale),
              total: projects.length,
            },
          ],
    [projects, props.groupBySong, props.songCounts, sort, text.locale],
  );
  const segments = useMemo(() => layoutSegments(groups, props.groupBySong), [groups, props.groupBySong]);
  const ordered = useMemo(() => groups.flatMap((g) => g.projects), [groups]);
  const ids = useMemo((): readonly ProjectId[] => ordered.map((p) => p.id), [ordered]);
  // The roving tab stop: the focused card when it is shown, else the first.
  const stop = ordered.some((p) => p.id === focused) ? focused : (ordered[0]?.id ?? null);

  const cards = useCallback(
    (): HTMLElement[] => [...(root.current?.querySelectorAll<HTMLElement>('[data-card-main]') ?? [])],
    [],
  );
  const cardOf = useCallback(
    (id: string): HTMLElement | null => cards().find((el) => el.getAttribute('data-project-id') === id) ?? null,
    [cards],
  );

  // The focused chart left the list: the keyboard goes to the next chart shown (its main button, also when the
  // card's menu button had focus).
  const handoff = useFocusHandoff({ ids, target: cardOf });

  // The tab stop of a chart that left the Library (Trash, deleted) moves to its neighbour as shown, not to the
  // first chart. A chart the search only hides keeps it (it comes back when the search is cleared).
  const shownBefore = useRef<readonly ProjectId[]>(ids);
  useLayoutEffect(() => {
    const prev = shownBefore.current;
    shownBefore.current = ids;
    const cur = store.get().focused;
    if (cur === null || ids.includes(cur)) return;
    if (liveProjects(store.get().projects).some((p) => p.id === cur)) return;
    const next = successorOf(prev, ids, cur);
    if (next !== null) store.update({ focused: next });
  }, [ids, store]);

  const focusCard = (el: HTMLElement | null | undefined): void => {
    if (el === null || el === undefined) return;
    el.focus();
    try {
      el.scrollIntoView({ block: 'nearest' });
    } catch {
      // Environments without layout (tests) have no scrollIntoView.
    }
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement;
    if (!target.hasAttribute('data-card-main')) return;
    const id = target.getAttribute('data-project-id') as ProjectId | null;
    const p = ordered.find((x) => x.id === id);
    if (p === undefined) return;
    const list = cards();
    const i = list.indexOf(target);
    const mod = e.ctrlKey || e.metaKey || e.altKey;
    // One action per press for keys that act on the chart (holding Delete never trashes a row of charts).
    const once = (run: () => void): void => {
      e.preventDefault();
      if (!e.repeat) run();
    };
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowLeft':
      case 'ArrowDown':
      case 'ArrowUp': {
        if (mod) return;
        e.preventDefault();
        const vertical = e.key === 'ArrowDown' || e.key === 'ArrowUp';
        const dir = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
        const geometric = vertical && view === 'grid' ? verticalNeighbour(list, target, dir) : null;
        focusCard(geometric ?? list[Math.min(list.length - 1, Math.max(0, i + dir))]);
        return;
      }
      case 'Home':
        if (mod) return;
        e.preventDefault();
        focusCard(list[0]);
        return;
      case 'End':
        if (mod) return;
        e.preventDefault();
        focusCard(list[list.length - 1]);
        return;
      case 'Enter':
      case ' ':
        // The card's own activation, done here so a global binding of the key (Space plays) never takes it.
        if (mod || e.shiftKey) return;
        once(() => {
          void openProject(services, p);
        });
        return;
      case 'F2':
        if (mod) return;
        once(() => {
          store.update({ renaming: p.id, focused: p.id });
        });
        return;
      case 'Delete':
        if (mod) return;
        once(() => {
          void trashProject(services, p);
        });
        return;
      default:
        if (!mod && e.key.length === 1 && e.key.trim() !== '') {
          e.preventDefault();
          props.onTypeToSearch(e.key);
        }
    }
  };

  return (
    <div
      ref={root}
      className={s.grid}
      data-view={view}
      role="group"
      aria-label={text.t(m.listLabel)}
      aria-describedby="library-keyboard-hint"
      onKeyDown={onKeyDown}
      onFocus={handoff.onFocus}
      onBlur={handoff.onBlur}
    >
      {segments.map((seg) => {
        // A song with one chart is just that chart: consecutive ones flow together in one grid or list, without
        // a song header each (UX-083); songs with several difficulties keep their header.
        const grouped = seg.kind === 'song';
        const cardsOf = seg.projects.map((p) => (
          <ProjectCard
            key={p.id}
            services={services}
            text={text}
            project={p}
            now={now}
            view={view}
            groupTitle={grouped ? seg.group.title : null}
            focused={p.id === stop}
            renaming={p.id === renaming}
            busy={busy.has(p.id)}
            onFocus={() => {
              if (store.get().focused !== p.id) store.update({ focused: p.id });
            }}
            onOpen={() => {
              void openProject(services, p);
            }}
            onRename={(name) => {
              store.update({ renaming: null });
              if (name !== null) void renameProject(services, p, name);
              // Give the keyboard back to the card.
              requestAnimationFrame(() => {
                cardOf(p.id)?.focus();
              });
            }}
          />
        ));
        return seg.kind === 'song' ? (
          <SongGroup key={seg.key} text={text} group={seg.group} view={view}>
            {cardsOf}
          </SongGroup>
        ) : (
          <ul key={seg.key} className={s.cards} data-view={view} role="list">
            {cardsOf}
          </ul>
        );
      })}
      <p id="library-keyboard-hint" className="visually-hidden">
        {text.t(m.keyboardHint)}
      </p>
    </div>
  );
}
